import {
  Box,
  DialogContent,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  CircularProgress,
  IconButton
} from '@mui/material';
import { useLiquidInstanceContext } from '../../context/liquidInstanceContext';
import Progress from './Progress';
import { useEffect, useState } from 'react';
import { useChainId, useSwitchChain, useWaitForTransactionReceipt, useWriteContract } from 'wagmi';
import { useAccount } from 'wagmi';
import { erc20Abi, relendWrappedAbi } from '../../assets/wagmi/generated';
import { readContract } from '@wagmi/core';
import { Address, formatUnits } from 'viem';
import styles from '../../styles/styles';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReportIcon from '@mui/icons-material/Report';
import { wagmiConfig } from '../../wagmiConfig';
function Approve({ setIsApproved }: { setIsApproved: (isApproved: boolean) => void }) {
  const { address } = useAccount();
  const { liquidInstanceConfiguration, normalizedAmount } = useLiquidInstanceContext();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const { data: hash, isPending, writeContract } = useWriteContract();
  const { isLoading: isApproving, isSuccess: isApproved } = useWaitForTransactionReceipt({
    hash
  });

  useEffect(() => {
    if (isApproved) {
      setIsApproved(true);
    }
  }, [isApproved, setIsApproved]);

  const getApprove = async () => {
    if (chainId !== liquidInstanceConfiguration.L1.chainId) {
      switchChain({ chainId: liquidInstanceConfiguration.L1.chainId });
    }

    try {
      if (!address) {
        throw new Error('No address found');
      }
      /// if allowance isn't enough, approve the amount
      writeContract({
        address: liquidInstanceConfiguration.L1.L1TokenAddress,
        abi: erc20Abi,
        functionName: 'approve',
        args: [liquidInstanceConfiguration.L2.L1WrappedTokenAddress as `0x${string}`, normalizedAmount]
      });
    } catch (error) {
      console.error('Approval failed:', error);
    }
  };
  return (
    <>
      {isApproving ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress />
          <Typography>Transaction is being processed</Typography>
        </Box>
      ) : (
        <Button sx={styles.gradientButton} onClick={getApprove} disabled={isPending || isApproved}>
          Approve
        </Button>
      )}
    </>
  );
}

function Wrap({ setIsWrapped }: { setIsWrapped: (isWrapped: boolean) => void }) {
  const { address } = useAccount();
  const { liquidInstanceConfiguration, normalizedAmount, setNormalizedAmount } = useLiquidInstanceContext();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();

  const { data: hash, isPending, writeContract, error: writeError } = useWriteContract();
  const {
    isLoading: isWrapping,
    isSuccess: isWrapped,
    isError: wrappingError,
    error: waitError
  } = useWaitForTransactionReceipt({
    hash
  });

  useEffect(() => {
    if (writeError || waitError) {
      console.error('Transaction failed:', writeError || waitError);
    }
  }, [writeError, waitError]);

  useEffect(() => {
    if (isWrapped) {
      setIsWrapped(true);
      setNormalizedAmount(BigInt(0));
    }
  }, [isWrapped, setIsWrapped, setNormalizedAmount]);

  const getWrap = async () => {
    if (chainId !== liquidInstanceConfiguration.L1.chainId) {
      await switchChain({ chainId: liquidInstanceConfiguration.L1.chainId });
    }

    try {
      if (!address) {
        throw new Error('No address found');
      }
      await writeContract({
        address: liquidInstanceConfiguration.L2?.L1WrappedTokenAddress as `0x${string}`,
        abi: relendWrappedAbi,
        functionName: 'depositFor',
        args: [address, normalizedAmount]
      });
    } catch (error) {
      console.error('Wrapping failed:', error);
    }
  };
  return (
    <>
      {wrappingError || writeError ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography sx={{ color: 'rgba(234, 83, 86, 1)' }}>
            <ReportIcon sx={{ color: 'rgba(234, 83, 86, 1)', fontSize: '1.5rem' }} />
            Error wrapping your tokens, please try again.
          </Typography>
          <Button sx={{ ...styles.gradientButton, width: '100%' }} onClick={getWrap} disabled={isPending || isWrapped}>
            Wrap
          </Button>
        </Box>
      ) : isWrapping && !wrappingError ? (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress />
          <Typography>Transaction is being processed</Typography>
        </Box>
      ) : (
        <Button sx={styles.gradientButton} onClick={getWrap} disabled={isPending || isWrapped}>
          Wrap
        </Button>
      )}
    </>
  );
}

export default function WrapDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { liquidInstanceConfiguration, normalizedAmount } = useLiquidInstanceContext();
  const { address } = useAccount();
  const [isApproved, setIsApproved] = useState(false);
  const [isWrapped, setIsWrapped] = useState(false);
  const [allowance, setAllowance] = useState(BigInt(0));
  const displayAmount = normalizedAmount
    ? formatUnits(normalizedAmount, liquidInstanceConfiguration.L1.L1TokenDecimals)
    : '0';

  useEffect(() => {
    const fetchAllowance = async () => {
      const config = wagmiConfig;
      const allowance = await readContract(config, {
        address: liquidInstanceConfiguration.L1.L1TokenAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address as Address, liquidInstanceConfiguration.L2?.L1WrappedTokenAddress as `0x${string}`]
      });
      setAllowance(allowance);
    };
    fetchAllowance();
  }, [
    address,
    liquidInstanceConfiguration.L1.L1TokenAddress,
    liquidInstanceConfiguration.L2?.L1WrappedTokenAddress as `0x${string}`,
    normalizedAmount,
    isApproved
  ]);

  useEffect(() => {
    if (allowance && allowance >= normalizedAmount) {
      setIsApproved(true);
    }
    if (allowance && allowance < normalizedAmount) {
      setIsApproved(false);
    }
  }, [allowance, normalizedAmount, setIsApproved, isApproved]);

  useEffect(() => {
    if (!open) {
      setIsApproved(false);
      setIsWrapped(false);
    }
  }, [open]);

  const handleClose = () => {
    setIsApproved(false);
    setIsWrapped(false);
    onClose();
  };

  return (
    <Dialog
      PaperProps={{
        sx: { ...styles.dialog }
      }}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle
        sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Progress isApproved={isApproved} isWrapped={isWrapped} steps={3} />
        <IconButton onClick={onClose}>
          <CloseIcon sx={{ color: 'rgba(255, 255, 255, 0.76)' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {!isApproved && (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6">Approve token wrapping</Typography>
              <Typography>First you need to allow the Relend contract to wrap your tokens.</Typography>
            </Box>
            <Box
              sx={{
                ...styles.greyBackground,
                padding: 1,
                borderRadius: 3,
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: 1
              }}
            >
              <Typography sx={{ ...styles.greyText }}>Allow the Relend contract to wrap</Typography>
              <Typography>
                {' '}
                {displayAmount} {liquidInstanceConfiguration.L1.L1Token}
              </Typography>
            </Box>
            <Approve setIsApproved={setIsApproved} />
          </>
        )}
        {isApproved && !isWrapped && (
          <>
            <Typography variant="h6">Wrap your tokens</Typography>
            <Typography>By wrapping your tokens, you will be able to bridge your assets.</Typography>
            <Box
              sx={{ ...styles.greyBackground, padding: 1, borderRadius: 2, display: 'flex', flexDirection: 'column' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
                <Typography sx={{ ...styles.greyText }}>You will deposit</Typography>
                <Typography>
                  {' '}
                  {displayAmount} {liquidInstanceConfiguration.L1.L1Token}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row' }}>
                <Typography sx={{ ...styles.greyText }}>You will receive</Typography>
                <Typography>
                  {' '}
                  {displayAmount} {liquidInstanceConfiguration.L2?.L2TokenName}
                </Typography>
              </Box>
            </Box>
            <Wrap setIsWrapped={setIsWrapped} />
          </>
        )}
        {isWrapped && (
          <>
            <Typography>Bridge</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <IconButton>
                <TaskAltIcon sx={{ color: 'rgba(4, 255, 218, 1)', fontSize: '4rem' }} />
              </IconButton>
              <Typography sx={{ color: 'rgba(4, 255, 218, 1)' }}>Assets successfully wrapped</Typography>
            </Box>
            <Typography>
              You're all set! To bridge your assets, continue to the {liquidInstanceConfiguration.L2.name} Bridge and
              complete the transfer securely.
            </Typography>
            <Button
              onClick={() => window.open(liquidInstanceConfiguration.L2?.BridgeUrl, '_blank')}
              sx={styles.gradientButton}
            >
              Bridge your assets
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
